import React, { useEffect, useRef } from "react";
import { Card, Box } from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import CourseFeedbackDialog from "./CourseFeedbackDialog";
import {
  submitCourseFeedback,
  checkCourseFeedback,
  getCourseAverageRating,
} from "../../Redux/CourseFeedback";

/* Formats ISO / Date values into: 21 Jan 2026 */
const formatDueDate = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* Checks if a course is overdue */
const isOverdue = (dueDate) => {
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  return due < today;
};

/* Status config */
const STATUS_CONFIG = {
  YET_TO_START: {
    icon: "/Assets/yetToStart-new.png",
    color: "#8E24AA",
  },
  IN_PROGRESS: {
    icon: "/Assets/inProgress.png",
    color: "#1976D2",
  },
  COMPLETED: {
    icon: "/Assets/completed.png",
    color: "#2E7D32",
  },
};

export default function LearningCard(props) {
  const { instance } = useMsal();
  const dispatch = useDispatch();
  const activeAccount = instance.getActiveAccount();
  const enterpriseId = activeAccount.idTokenClaims.enterpriseID;
  const feedbackCheckedRef = useRef(false);
  const ratingFetchedRef = useRef(false);

  const course = props.course;
  const path = props?.path;

  const modulesText = Array.isArray(course?.modules)
    ? course.modules.join(", ")
    : course?.modules;

  const overdue = isOverdue(course?.dueDate);

  const statusKey =
    course?.status ||
    (!course?.enrollmentDate && !course?.completionDate
      ? "YET_TO_START"
      : course?.enrollmentDate && !course?.completionDate
        ? "IN_PROGRESS"
        : "COMPLETED");

  const status = STATUS_CONFIG[statusKey];

  /* ---------------- FEEDBACK LOGIC ---------------- */
  const [openFeedback, setOpenFeedback] = useState(false);

  const feedbackSubmitted = useSelector((state) => {
    const key = `${enterpriseId}__${course?.courseName}`;
    return state.CourseFeedback.feedbackStatus?.[key];
  });

  const courseRating = useSelector((state) => {
    const key = `${enterpriseId}__${course?.courseName}`;
    return state.CourseFeedback.ratingByCourse?.[key];
  });

  const handleCTAClick = (e) => {
    if (statusKey === "COMPLETED") {
      e.preventDefault();
      setOpenFeedback(true);
    }
  };

  useEffect(() => {
    if (
      statusKey === "COMPLETED" &&
      enterpriseId &&
      course?.courseName &&
      !feedbackCheckedRef.current &&
      feedbackSubmitted === undefined
    ) {
      feedbackCheckedRef.current = true;
      dispatch(checkCourseFeedback(enterpriseId, course.courseName));
    }
  }, [
    statusKey,
    enterpriseId,
    course?.courseName,
    feedbackSubmitted,
    dispatch,
  ]);

  useEffect(() => {
    if (
      enterpriseId &&
      course?.courseName &&
      !ratingFetchedRef.current &&
      !courseRating
    ) {
      ratingFetchedRef.current = true;
      dispatch(getCourseAverageRating(enterpriseId, course.courseName));
    }
  }, [enterpriseId, course?.courseName, courseRating, dispatch]);

  return (
    <Card
      className="my-learning-cards-holder"
      sx={{
        width: 260,
        borderRadius: 0,
        flexShrink: 0,
        // boxShadow: "0 4px 8px rgba(0,0,0,0.2), 0 6px 10px rgba(0,0,0,0.19)",
        "@media (max-width: 600px)": {
          width: 199,
        },
      }}
    >
      <div className="learning-card">
        {/* Image */}
        <div className="learning-card-image" style={{ position: "relative" }}>
          <img src={props?.courseImg?.img} alt="course" />

          {/* Due / Overdue Tag */}
          {statusKey === "IN_PROGRESS" &&
            course?.dueDate &&
            (overdue ? (
              <span
                style={{
                  position: "absolute",
                  top: "8px",
                  left: "0px",
                  backgroundColor: "#d32f2f",
                  color: "#fff",
                  fontSize: "0.7rem",
                  padding: "4px 6px",
                  fontWeight: 700,
                }}
              >
                Pending
              </span>
            ) : (
              <span
                style={{
                  position: "absolute",
                  top: "8px",
                  left: "0px",
                  backgroundColor: "#CE7A41",
                  color: "#fff",
                  fontSize: "0.7rem",
                  padding: "4px 6px",
                  fontWeight: 600,
                }}
              >
                Due by {formatDueDate(course.dueDate)}
              </span>
            ))}
        </div>

        {/* Content */}
        <div className="learning-card-content">
          <div
            className="learning-card-top"
            style={{
              marginBottom: "10px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <div className="badges">
              {courseRating?.count > 0 && (
                <span
                  className="badge rating"
                  style={{ backgroundColor: "#1269A0" }}
                >
                  ⭐ {courseRating?.avgRating}
                </span>
              )}
              <span
                className="badge points"
                style={{ backgroundColor: "#1269A0" }}
              >
                🏅 {course?.coursePoints} Points
              </span>

              {course?.courseType === "Mandatory" && (
                <span
                  style={{
                    backgroundColor: "#CE7A41",
                    color: "#fff",
                    fontSize: "12px",
                    padding: "2px 6px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Mandatory
                </span>
              )}
            </div>
          </div>

          {path === "homepage" && (
            <p
              className="courseCategory"
              style={{
                color: "#a100ff",
                textTransform: "uppercase",
                fontSize: "12px",
                marginTop: "0.5rem",
                marginBottom: "0.5rem",
              }}
            >
              {course?.category}
            </p>
          )}

          {path === "homepage" && (
            <span
              className="category"
              style={{
                color: "#a100ff",
                fontSize: "14px",
                marginBottom: "1rem",
              }}
            >
              {modulesText}
              <Box
                sx={{
                  mt: "2px",
                  height: "1.5px",
                  width: "20%",
                  borderRadius: "2px",
                  backgroundColor: "#a100ff",
                  transition: "all 0.3s ease",
                }}
              />
            </span>
          )}

          <div
            style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}
          >
            <p className="title" style={{ fontSize: "0.8rem" }}>
              {course?.courseName}
            </p>

            <div style={{ marginTop: "auto" }}>
              <p
                className="description hide-scrollbar"
                style={{
                  height: "4rem",
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                {course?.courseDesc}
              </p>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "10px",
                  color: status.color,
                  fontSize: "12px",
                  fontWeight: 600,
                }}
              >
                <div className="avatars">
                  <img
                    src={`${process.env.PUBLIC_URL}/Assets/Home/CardAvatar1.png`}
                    alt=""
                  />
                  <img
                    src={`${process.env.PUBLIC_URL}/Assets/Home/CardAvatar2.png`}
                    alt=""
                  />
                  <img
                    src={`${process.env.PUBLIC_URL}/Assets/Home/CardAvatar3.png`}
                    alt=""
                  />
                  <span className="count" style={{ color: "black" }}>
                    +15
                  </span>
                </div>

                <img
                  src={process.env.PUBLIC_URL + status.icon}
                  alt=""
                  style={{ width: 80, marginTop: "10px" }}
                />
              </div>

              <div className="meta">
                <span>⏱ {course?.durationHrs} Hrs</span>
                <span style={{ display: "flex", alignItems: "center" }}>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/TeamAndLead/Lessons.svg`}
                    style={{ marginRight: "5px", marginBottom: "2px" }}
                    alt=""
                  />
                  {course?.totalLessons === 0 ? 1 : course?.totalLessons}{" "}
                  Lessons
                </span>
              </div>

              <a
                href={course?.courseLink}
                onClick={handleCTAClick}
                target={
                  statusKey === "YET_TO_START" || statusKey === "IN_PROGRESS"
                    ? "_blank"
                    : "_self"
                }
                rel={
                  statusKey === "YET_TO_START" || statusKey === "IN_PROGRESS"
                    ? "noopener noreferrer"
                    : undefined
                }
                style={{ pointerEvents: feedbackSubmitted ? "none" : "auto" }}
              >
                <button
                  className="learn-btn"
                  style={{
                    backgroundColor: "#050505",
                    color: "#ffffff",
                    width: "100%",
                    justifyContent: "center",
                    marginTop: "6px",
                    opacity: feedbackSubmitted ? 0.6 : 1,
                  }}
                >
                  <span>→</span> {statusKey === "YET_TO_START" && "Enroll Now"}
                  {statusKey === "IN_PROGRESS" && "Continue"}
                  {statusKey === "COMPLETED" &&
                    (feedbackSubmitted
                      ? "Feedback Submitted"
                      : "Share your Feedback")}
                </button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* FEEDBACK MODAL */}
      <CourseFeedbackDialog
        open={openFeedback}
        onClose={() => setOpenFeedback(false)}
        courseName={course?.courseName}
        onSubmit={({ rating }) => {
          dispatch(
            submitCourseFeedback({
              enterpriseId: enterpriseId,
              courseName: course.courseName,
              hubName: course.hubName,
              category: course.category,
              modules: modulesText,
              rating,
            }),
          );
          setOpenFeedback(false);
        }}
      />
    </Card>
  );
}
